import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { PHOTOS, LANA_TRIBUTE } from '@/data/site';
import { useScrollLock } from '@/lib/experience';
import { Heart, Paw, Cloudy, Bow } from './brand/Shapes';

const INK = '#5B3A29'; // marrom quentinho, como na homenagem original
const SCRIPT = { fontFamily: '"Great Vibes", cursive' };
const SERIF = { fontFamily: '"Cormorant Garamond", Georgia, serif' };

let fontsRequested = false;
/** Carrega as fontes da homenagem só quando precisa (não pesa o resto do site). */
export const ensureTributeFonts = () => {
  if (fontsRequested || typeof document === 'undefined') return;
  fontsRequested = true;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400..700;1,400..700&family=Great+Vibes&display=swap';
  document.head.appendChild(link);
};

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] },
});

const Divider = ({ delay }) => (
  <motion.div className="flex items-center justify-center gap-2 my-5" style={{ color: '#C98B8B' }} {...fadeUp(delay)} aria-hidden="true">
    <span className="h-px w-10 bg-current opacity-50" />
    <span className="w-1 h-1 rounded-full bg-current opacity-60" />
    <Heart className="w-3.5 h-3.5" />
    <span className="w-1 h-1 rounded-full bg-current opacity-60" />
    <span className="h-px w-10 bg-current opacity-50" />
  </motion.div>
);

/** Arco-íris suave ao fundo (a "ponte do arco-íris"). */
const Rainbow = () => (
  <motion.svg
    viewBox="0 0 600 320"
    className="absolute -top-10 right-[-12%] w-[90vw] max-w-[900px] opacity-0"
    animate={{ opacity: 0.55 }}
    transition={{ duration: 3, delay: 0.6 }}
    aria-hidden="true"
    style={{ filter: 'blur(3px)' }}
  >
    {['#F7B4B4', '#F8CFA3', '#F7E6A1', '#C9E6B3', '#B6DAF2', '#C9C0EE'].map((c, i) => (
      <path key={c} d={`M ${40 + i * 14} 320 A ${260 - i * 14} ${260 - i * 14} 0 0 1 ${560 - i * 14} 320`} fill="none" stroke={c} strokeWidth="14" />
    ))}
  </motion.svg>
);

export const LanaTribute = ({ open, onClose }) => {
  useScrollLock(open);
  const [hearts, setHearts] = useState([]);

  useEffect(() => {
    if (!open) return undefined;
    ensureTributeFonts();
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // estrelinhas douradas e patinhas subindo (posições fixas por abertura)
  const sparkles = useMemo(
    () => Array.from({ length: 34 }, (_, i) => ({ i, l: Math.random() * 100, t: Math.random() * 100, s: 2 + Math.random() * 3.5, d: Math.random() * 4, dur: 2.5 + Math.random() * 3 })),
    []
  );
  const paws = useMemo(() => Array.from({ length: 7 }, (_, i) => ({ i, l: 6 + i * 14, dur: 14 + (i % 3) * 4, delay: i * 2.2 })), []);

  const sendLove = () => {
    const batch = Array.from({ length: 7 }, (_, k) => ({ id: Math.random(), x: (k - 3) * 18 + (Math.random() * 10 - 5), s: 14 + Math.random() * 12 }));
    setHearts((h) => [...h, ...batch]);
    setTimeout(() => setHearts((h) => h.filter((x) => !batch.includes(x))), 3600);
  };

  const [l1, l2, l3a, l3b, l4] = LANA_TRIBUTE.lines;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[135] overflow-y-auto overflow-x-hidden"
          data-lenis-prevent
          role="dialog"
          aria-modal="true"
          aria-label="Homenagem em memória de Lana"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9 }}
          style={{ background: 'radial-gradient(ellipse at 68% 18%, #FFFBF2 0%, #FBEBD8 42%, #F1DDD6 72%, #E4D6EC 100%)', color: INK }}
        >
          {/* céu: raios de luz, arco-íris, nuvens, brilhos e patinhas */}
          <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
            <motion.div
              className="absolute left-1/2 top-[-40%] w-[160vmax] h-[160vmax] -ml-[80vmax] opacity-40"
              style={{ background: 'repeating-conic-gradient(from 0deg at 50% 50%, rgba(255,236,196,0.55) 0deg 6deg, transparent 6deg 18deg)', maskImage: 'radial-gradient(circle, #000 0%, transparent 60%)', WebkitMaskImage: 'radial-gradient(circle, #000 0%, transparent 60%)' }}
              animate={{ rotate: 360 }}
              transition={{ duration: 240, repeat: Infinity, ease: 'linear' }}
            />
            <Rainbow />
            {[
              { cls: 'bottom-[-4%] left-[-8%] w-[46vw]', o: 0.95, dur: 26 },
              { cls: 'bottom-[-6%] right-[-10%] w-[52vw]', o: 0.9, dur: 32 },
              { cls: 'bottom-[8%] left-[30%] w-[30vw]', o: 0.7, dur: 22 },
              { cls: 'top-[12%] left-[-6%] w-[22vw]', o: 0.55, dur: 28 },
            ].map((c, i) => (
              <motion.div
                key={i}
                className={`absolute text-white ${c.cls}`}
                style={{ opacity: c.o, filter: 'blur(6px)' }}
                animate={{ x: [0, i % 2 ? -30 : 30, 0] }}
                transition={{ duration: c.dur, repeat: Infinity, ease: 'easeInOut' }}
              >
                <Cloudy className="w-full" />
              </motion.div>
            ))}
            {sparkles.map((p) => (
              <motion.span
                key={p.i}
                className="absolute rounded-full"
                style={{ left: `${p.l}%`, top: `${p.t}%`, width: p.s, height: p.s, background: '#F6D08A', boxShadow: '0 0 8px 2px rgba(246,208,138,0.8)' }}
                animate={{ opacity: [0.15, 1, 0.15], scale: [0.7, 1.1, 0.7] }}
                transition={{ duration: p.dur, delay: p.d, repeat: Infinity, ease: 'easeInOut' }}
              />
            ))}
            {paws.map((p) => (
              <motion.span
                key={p.i}
                className="absolute bottom-[-40px] w-5 h-5"
                style={{ left: `${p.l}%`, color: 'rgba(91,58,41,0.18)' }}
                animate={{ y: ['0vh', '-115vh'], opacity: [0, 1, 1, 0], rotate: [p.i % 2 ? -14 : 14, p.i % 2 ? 10 : -10] }}
                transition={{ duration: p.dur, delay: p.delay, repeat: Infinity, ease: 'linear' }}
              >
                <Paw className="w-full h-full" />
              </motion.span>
            ))}
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar homenagem"
            className="fixed top-4 right-4 md:top-6 md:right-6 z-20 w-12 h-12 rounded-full bg-white/80 backdrop-blur-sm shadow-md text-2xl leading-none hover:bg-white transition-colors"
            style={{ color: INK }}
          >
            ×
          </button>

          <div className="relative z-10 min-h-full container-cp py-16 md:py-16 grid md:grid-cols-2 gap-x-14 gap-y-8 items-center [grid-template-areas:'photo'_'intro'_'closing'] md:[grid-template-areas:'intro_photo'_'intro_closing']">
            {/* texto */}
            <div className="[grid-area:intro] text-center">
              <motion.div className="relative inline-block" {...fadeUp(0.4)}>
                <motion.svg viewBox="0 0 60 54" className="absolute -left-14 -top-5 md:-left-[4.5rem] md:-top-10 w-10 md:w-12" aria-hidden="true"
                  animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 4, repeat: Infinity }}>
                  <path d="M30 50 C 10 36, 2 26, 2 15 C 2 7, 8 2, 15 2 C 22 2, 27 7, 30 12 C 33 7, 38 2, 45 2 C 52 2, 58 7, 58 15 C 58 26, 50 36, 30 50 Z"
                    fill="none" stroke="#fff" strokeWidth="2.5" style={{ filter: 'drop-shadow(0 0 6px rgba(255,214,150,0.95))' }} />
                </motion.svg>
                <p className="text-2xl md:text-3xl tracking-wide" style={SERIF}>Em memória de</p>
              </motion.div>

              {/* nome caligrafado */}
              <motion.h2
                className="font-normal leading-[1.15] text-[5.5rem] md:text-[8.5rem] mt-2 px-6 py-2"
                style={{ ...SCRIPT, color: INK }}
                initial={{ clipPath: 'inset(-40% 100% -60% -40%)', opacity: 1 }}
                animate={{ clipPath: 'inset(-40% -40% -60% -40%)' }}
                transition={{ duration: 2.2, delay: 0.9, ease: [0.45, 0, 0.2, 1] }}
              >
                {LANA_TRIBUTE.name}
              </motion.h2>
              <div className="relative mx-auto mt-1 md:mt-2 w-[250px] md:w-[320px]" aria-hidden="true">
                <svg viewBox="0 0 320 40" className="w-full">
                  <motion.path
                    d="M8 22 C 60 30, 110 30, 150 20 C 175 12, 200 12, 205 20 C 210 30, 185 34, 175 24 C 168 16, 190 8, 225 14 C 255 19, 280 24, 300 20"
                    fill="none"
                    stroke={INK}
                    strokeWidth="2"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1.8, delay: 2.6, ease: 'easeInOut' }}
                  />
                </svg>
                <motion.span className="absolute -right-4 -top-1 w-6 h-6" style={{ color: INK }} initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 4.1, duration: 0.8 }}>
                  <Paw className="w-full h-full" />
                </motion.span>
              </div>

              <motion.p className="mt-6 text-[1.3rem] md:text-2xl leading-snug max-w-md mx-auto" style={{ ...SERIF, fontWeight: 500 }} {...fadeUp(3)}>
                {l1} <Heart className="inline w-4 h-4 -mt-1" style={{ color: '#E7A1A8' }} />
              </motion.p>
              <Divider delay={3.4} />
              <motion.p className="text-[1.45rem] md:text-[1.7rem] italic leading-snug max-w-md mx-auto" style={{ ...SERIF, fontWeight: 500 }} {...fadeUp(3.8)}>
                {l2}
              </motion.p>
              <Divider delay={4.2} />
              <motion.p className="text-[1.3rem] md:text-2xl leading-snug" style={{ ...SERIF, fontWeight: 500 }} {...fadeUp(4.6)}>
                {l3a}
                <br />
                <span className="text-[2.4rem] md:text-5xl leading-tight" style={SCRIPT}>{l3b}</span>
              </motion.p>
              <motion.div className="mt-3 flex justify-center" {...fadeUp(5)} aria-hidden="true">
                <Heart className="w-5 h-5" style={{ color: '#E7A1A8' }} />
              </motion.div>
            </div>

            {/* foto em arco com halo de luz */}
            <div className="[grid-area:photo] flex flex-col items-center md:self-end">
              <motion.div
                className="relative w-[72vw] max-w-[380px]"
                initial={{ opacity: 0, y: 30, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              >
                {/* auréola */}
                <motion.div
                  className="absolute left-1/2 -top-7 w-28 h-7 -ml-14 rounded-[50%] border-[3px] border-[#F6D08A] z-10"
                  style={{ boxShadow: '0 0 18px 4px rgba(246,208,138,0.8), inset 0 0 10px rgba(246,208,138,0.6)' }}
                  animate={{ y: [0, -6, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                  aria-hidden="true"
                />
                <motion.div
                  className="relative aspect-[3/4] rounded-t-full rounded-b-[40px] overflow-hidden border-[8px] border-[#FFF8EE]"
                  animate={{ boxShadow: ['0 0 50px 10px rgba(255,214,150,0.55)', '0 0 90px 22px rgba(255,214,150,0.85)', '0 0 50px 10px rgba(255,214,150,0.55)'] }}
                  transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <img src={PHOTOS.lana.lg} srcSet={PHOTOS.lana.srcSet} sizes="380px" alt={PHOTOS.lana.alt} className="w-full h-full object-cover object-[50%_40%]" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(251,235,216,0.55), transparent 35%), radial-gradient(ellipse at 50% 0%, rgba(255,250,235,0.35), transparent 60%)' }} />
                </motion.div>
                <span className="absolute -top-2 -right-4 w-16 -rotate-12 text-[#F4A6C0] drop-shadow" aria-hidden="true">
                  <Bow className="w-full" />
                </span>
              </motion.div>
            </div>

            {/* mensagem final e despedida */}
            <div className="[grid-area:closing] flex flex-col items-center md:self-start">
              <motion.div className="mt-8 text-center max-w-md" {...fadeUp(5.4)}>
                <Paw className="w-6 h-6 mx-auto mb-3" style={{ color: INK }} />
                <p className="text-[1.3rem] md:text-2xl leading-snug" style={{ ...SERIF, fontWeight: 500 }}>
                  {l4} <Heart className="inline w-4 h-4 -mt-1" style={{ color: '#E7A1A8' }} />
                </p>
              </motion.div>
              <Divider delay={5.8} />
              <motion.p className="text-center leading-none" {...fadeUp(6.2)}>
                <span className="block text-3xl md:text-4xl" style={{ ...SERIF, fontWeight: 500 }}>{LANA_TRIBUTE.farewell[0]}</span>
                <span className="inline-flex items-center gap-2 text-[3.4rem] md:text-[4.2rem]" style={SCRIPT}>
                  {LANA_TRIBUTE.farewell[1]}
                  <Heart className="w-6 h-6" style={{ color: '#E7A1A8' }} />
                </span>
              </motion.p>

              {/* deixar um carinho */}
              <motion.div className="relative mt-8" {...fadeUp(6.8)}>
                <button
                  type="button"
                  onClick={sendLove}
                  className="relative inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur-sm px-6 py-3 shadow-md hover:bg-white transition-colors text-lg"
                  style={{ ...SERIF, fontWeight: 600, color: INK }}
                >
                  <Heart className="w-4 h-4" style={{ color: '#E7A1A8' }} /> Deixar um carinho para a Lana
                </button>
                <div className="pointer-events-none absolute left-1/2 top-0" aria-hidden="true">
                  <AnimatePresence>
                    {hearts.map((h) => (
                      <motion.span
                        key={h.id}
                        className="absolute"
                        style={{ width: h.s, height: h.s, marginLeft: -h.s / 2, color: '#E7A1A8' }}
                        initial={{ x: 0, y: 0, opacity: 0, scale: 0.4 }}
                        animate={{ x: h.x, y: -260, opacity: [0, 1, 1, 0], scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 3.2, ease: 'easeOut' }}
                      >
                        <Heart className="w-full h-full" />
                      </motion.span>
                    ))}
                  </AnimatePresence>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default LanaTribute;
